import { LowerCasePipe, NgTemplateOutlet } from '@angular/common';
import { Component, computed } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatSortModule } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { ContentComponent } from '@campaign-form/form-fields/creators-and-ctas/deliverable-flow-dialog/deliverable-step/deliverable-list/content/content.component';
import { AnalyticsDetailsTableComponent } from '@cta-analytics-dialog/deliverable-analytics-content/analytics-details-table/analytics-details-table.component';
import { DeliverableFindingsStore } from '@cta-analytics-dialog/deliverable-analytics-content/deliverable-findings.store';
import { TimelineGraphComponent } from '@cta-analytics-dialog/deliverable-analytics-content/graphs/timeline-graph/timeline-graph.component';
import { ITimelineGraphInputData } from '@cta-analytics-dialog/deliverable-analytics-content/graphs/timeline-graph/timeline-graph.interface';
import { EDeliverableType } from '@enums/campaign.enum';
import { IContentAnalytics } from '@interfaces/deliverable-analytics.interface';
import { TranslateModule } from '@ngx-translate/core';
import { DotsSpinnerComponent } from '@shared/components/dots-spinner/dots-spinner.component';
import { IconComponent } from '@shared/components/icon/icon.component';
import { StatusBadgeComponent } from '@shared/components/status-badge/status-badge.component';
import { DurationUtilitiesService } from '@utilities/duration-utilities.service';
import { MultiMediaPostAnalyticsBaseDirective } from '../multi-media-post-analytics-base/multi-media-post-analytics-base.directive';
import { PostMediaViewerComponent } from '../post-media-viewer/post-media-viewer.component';
import { IContentPostAnalyticsTableData } from './content-post-analytics.interface';

@Component({
  imports: [
    NgTemplateOutlet,
    TranslateModule,
    TimelineGraphComponent,
    AnalyticsDetailsTableComponent,
    MatTableModule,
    MatSortModule,
    MatButtonModule,
    StatusBadgeComponent,
    PostMediaViewerComponent,
    ContentComponent,
    DotsSpinnerComponent,
    IconComponent,
    LowerCasePipe,
  ],
  providers: [DeliverableFindingsStore],
  selector: 'app-content-post-analytics',
  templateUrl: './content-post-analytics.component.html',
})
export class ContentPostAnalyticsComponent extends MultiMediaPostAnalyticsBaseDirective<
  IContentPostAnalyticsTableData,
  IContentAnalytics
> {
  public override readonly deliverableType = EDeliverableType.content;
  public override readonly displayedColumns: string[] = ['timestamp', 'status', 'actions'];
  public override readonly dataSource: MatTableDataSource<IContentPostAnalyticsTableData> = new MatTableDataSource();

  public override readonly currentDeliverable = computed(() => {
    const deliverableId = this.deliverableId();
    const executionAnalytics = this.executionAnalyticsWithFindings();

    if (deliverableId && executionAnalytics) {
      return executionAnalytics?.ctaAnalytics?.contents?.find((c) => c.deliverableId === deliverableId);
    }

    return null;
  });

  public readonly currentMedia = computed(() => {
    const { src } = this.videoConfiguration();
    const mediaList = this.media();
    let found = mediaList.find((m) => m.url === src);

    if (!found && mediaList.length > 0) {
      [found] = mediaList;
    }

    return found;
  });

  public override readonly kpiCardData = null;

  protected override setCounts(): void {
    return;
  }

  protected override setKeywordList(): void {
    return;
  }

  protected override getKeywords(): string[] {
    return [];
  }

  protected override prepareChartAndTableData(): void {
    const tableData: IContentPostAnalyticsTableData[] = [];
    const dotGraphData: ITimelineGraphInputData[] = [];
    const currentContentAnalytics = this.currentDeliverable();
    const removedIds = this.manualFindingStatusStore?.serverRemovedIds() ?? new Set<string>();
    const findings = (currentContentAnalytics?.findings || []).filter((f) => !removedIds.has(f.id));
    let imageDotIndex = 0;

    findings.forEach((finding) => {
      const { id } = finding;
      const isImage = !this.media()[finding.mediaIndex ?? 0]?.isVideo;
      const videoTime = isImage ? 0 : (finding.videoTime ?? 0);
      const mediaIndex = finding.mediaIndex ?? 0;
      const aggregatedVideoTime = isImage ? 0 : this.calculateAggregatedVideoTime(mediaIndex, videoTime);
      const xAxisValue = isImage ? imageDotIndex++ : aggregatedVideoTime;

      dotGraphData.push({
        id,
        mediaIndex,
        status: currentContentAnalytics?.status!,
        videoTime,
        xAxisValue,
      });

      tableData.push({
        aggregatedVideoTime,
        findingParentId: finding.parentId,
        id,
        mediaIndex,
        status: currentContentAnalytics?.status ?? '',
        timestamp: isImage
          ? DurationUtilitiesService.convertSecondsToVideoTime(imageDotIndex, true)
          : DurationUtilitiesService.convertSecondsToVideoTime(aggregatedVideoTime, true),
        videoTime,
      });
    });

    tableData.sort((a, b) => a.aggregatedVideoTime - b.aggregatedVideoTime);

    this.dataSource.data = tableData;
    this.dataCount.set(tableData.length);
    this.dotGraphData.set([...dotGraphData].sort((a, b) => a.xAxisValue - b.xAxisValue));
  }
}
